import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { Brand } from './Brand'
import { CountryNotice, CountrySwitcher } from './CountrySwitcher'
import { NotificationBell } from './NotificationBell'
import { ThemeToggle } from './ThemeToggle'
import { UserMenu } from './UserMenu'
import { cx } from './ui/primitives'

interface NavItem {
  to: string
  label: string
  /**
   * Hidden when the user holds none of these. A screen that only ever 403s is not a screen.
   *
   * For the screens that administer something, this is deliberately the permission that
   * changes it rather than the one that reads it. Almost everybody holds the reads: a
   * quotation cannot be built without the catalogue, the resellers, the terms document and
   * the chain that will approve it. Listing a screen for everyone who can read what it
   * shows put six administration entries in a Manager's sidebar, none of which were theirs
   * to use.
   */
  permissions?: string[]
}

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: 'Work',
    items: [
      { to: '/', label: 'Dashboard' },
      { to: '/quotations', label: 'Quotations', permissions: ['quotation.view.own', 'quotation.view.team', 'quotation.view.any'] },
      { to: '/leads', label: 'Leads', permissions: ['lead.view.own', 'lead.view.team', 'lead.view.any'] },
      { to: '/approvals', label: 'Approvals', permissions: ['quotation.approve.assigned', 'quotation.approve.any'] },
      { to: '/customers', label: 'Customers', permissions: ['customer.view.own', 'customer.view.team', 'customer.view.any'] },
    ],
  },
  {
    section: 'Insight',
    items: [
      { to: '/analytics', label: 'Performance', permissions: ['analytics.view.own', 'analytics.view.team', 'analytics.view.any'] },

      // Hidden rather than shown-and-refused, and the distinction matters here more than
      // anywhere else on this rail. The reason commission is restricted is a partner's
      // privacy, not a division of labour, so a Manager should not learn that a commission
      // screen exists for a partner whose rates are none of their business.
      { to: '/commission', label: 'Commission', permissions: ['reseller.commission.view'] },
    ],
  },
  {
    section: 'Master data',
    items: [
      {
        to: '/products',
        label: 'Products',
        permissions: [
          'product.create', 'product.edit', 'product.archive', 'product.delete',
          'product.price.edit', 'product.price.editcost',
        ],
      },
      {
        to: '/resellers',
        label: 'Resellers',
        permissions: ['reseller.create', 'reseller.edit', 'reseller.archive'],
      },
      { to: '/countries', label: 'Countries', permissions: ['country.create', 'country.edit'] },
      { to: '/lists', label: 'Lists', permissions: ['reference.manage'] },
      {
        to: '/terms',
        label: 'Terms',
        permissions: [
          'terms.version.upload', 'terms.publish', 'terms.archive',
          'terms.default.set', 'terms.document.delete',
        ],
      },
      // Listed to whoever builds forms, not to everybody who works the leads they produce -
      // the same rule as every other administration screen on this rail.
      { to: '/lead-forms', label: 'Contact Forms', permissions: ['leadform.manage'] },
      { to: '/lead-sources', label: 'Ad Connections', permissions: ['leadintake.manage'] },
      {
        to: '/email-templates',
        label: 'Email Templates',
        permissions: ['emailtemplate.manage'],
      },
      {
        to: '/approval-chains',
        label: 'Approvals Setup',
        permissions: ['approvalchain.create', 'approvalchain.edit', 'approvalchain.activate'],
      },
    ],
  },
  {
    section: 'Administration',
    items: [
      {
        to: '/users',
        label: 'Users',
        permissions: [
          'user.invite', 'user.edit', 'user.deactivate',
          'user.role.assign', 'user.permission.override', 'user.country.assign',
        ],
      },
      { to: '/teams', label: 'Sales Teams', permissions: ['salesteam.manage'] },
    ],
  },
]

export function AppShell() {
  const { canAny } = useAuth()

  const sections = NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.permissions || canAny(...item.permissions)),
  })).filter((section) => section.items.length > 0)

  return (
    <div className="flex min-h-full">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <div className="px-5 py-5">
          <Brand className="h-6" />
        </div>

        <nav className="flex-1 px-3">
          {sections.map((section) => (
            <div key={section.section} className="mb-5">
              <p className="px-2 pb-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-faint">
                {section.section}
              </p>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cx(
                      'mb-0.5 block rounded-md px-2.5 py-1.5 text-sm transition-colors',
                      isActive
                        ? 'brand-gradient font-medium text-white'
                        : 'text-ink-2 hover:bg-sunken',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-line bg-surface px-5 py-2">
          <div className="lg:hidden">
            <Brand className="h-5" />
          </div>

          <CountrySwitcher />

          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <CountryNotice />

        {/* Mobile navigation: the sidebar is desktop-only, and a rep on a phone still
            needs to reach the same screens. The full mobile experience is the Expo
            app in M10; this is so the web app is not unusable on a small screen. */}
        <nav className="flex gap-1 overflow-x-auto border-b border-line bg-surface px-3 py-2 lg:hidden">
          {sections.flatMap((section) => section.items).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cx(
                  'whitespace-nowrap rounded-md px-3 py-1.5 text-[13px]',
                  isActive ? 'brand-gradient text-white' : 'text-ink-2 hover:bg-sunken',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Full width, no centred column. A quotation table is a wide thing, and a
            reader on a 27-inch screen should not be reading it through a letterbox. */}
        <main className="flex-1 px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
